import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { type OAuth2AuthorizeRequestBody, useGetAccountAppScopesQuery, useGetOAuth2AppByClientIdQuery, useOauth2AuthorizeMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useGetPermissionsQuery } from '../../store/role/role'
import { removeToast, selectToasts } from '../../store/toast/toastSlice'
import { type AppDispatch, type RootState } from '../../store/store'
import { useGetMeQuery } from '../../store/user/user'
import './Authorize.scss'
import { OAuthConsentView } from './components/OAuthConsentView'
import { OAuthErrorView } from './components/OAuthErrorView'
import { OAuthSuccessView } from './components/OAuthSuccessView'
import { isAppEnabledForUser } from './utils/checkAppPermission'
import {
  buildAppPermissionDeniedError,
  type ClassifiedError,
  classifyAuthorizeError,
  classifyGetAppError,
  classifyValidationErrors,
} from './utils/classifyError'
import { parseAndValidateParams } from './utils/parseParams'
import { buildScopeGroups } from './utils/scopeMetadata'

type Screen = 'loading' | 'consent' | 'success' | 'error'

export const OAuthAuthorize: React.FC = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [searchParams] = useSearchParams()
  const [authorizeError, setAuthorizeError] = useState<ClassifiedError | null>(null)
  const [successRedirectUri, setSuccessRedirectUri] = useState<string | null>(null)

  const { params, validationErrors } = useMemo(() => parseAndValidateParams(searchParams), [searchParams])

  const {
    isFetching: isFetchingApp,
    data: oauth2AppData,
    error: oauth2AppError,
    isUninitialized: isAppUninitialized,
  } = useGetOAuth2AppByClientIdQuery({ accountId, clientId: params?.clientId ?? '' }, { skip: !params?.clientId })
  const oauth2App = oauth2AppData ?? null

  const { data: currentUser, isFetching: isFetchingMe } = useGetMeQuery({ accountId })
  const { data: permissions, isFetching: isFetchingPermissions } = useGetPermissionsQuery()

  const {
    data: accountAppScopesData,
    isFetching: isFetchingScopes,
    error: scopesError,
  } = useGetAccountAppScopesQuery({ accountId, clientId: oauth2App?.clientId ?? '' }, { skip: !oauth2App })
  const availableScopes = accountAppScopesData?.data?.scopes ?? null

  const [userToggles, setUserToggles] = useState<Map<string, boolean>>(new Map())

  const selectedScopes = useMemo(() => {
    if (!availableScopes || !params) {
      return new Set<string>()
    }
    const availableScopeSet = new Set(availableScopes)
    const urlScopes = new Set(params.scopes)
    const result = new Set(availableScopes.filter((s) => urlScopes.has(s)))
    userToggles.forEach((checked, scope) => {
      if (!availableScopeSet.has(scope)) {
        return
      }
      if (checked) {
        result.add(scope)
      } else {
        result.delete(scope)
      }
    })
    return result
  }, [availableScopes, params, userToggles])

  const [triggerOAuth2Authorize, { isLoading: isAuthorizing, isSuccess: isAuthorizeSuccess }] = useOauth2AuthorizeMutation()

  const userRoleIds = useMemo(() => currentUser?.roles?.map((r) => r.id) ?? [], [currentUser?.roles])
  const userName = currentUser?.me ?? currentUser?.name ?? ''

  const appPermissionDenied = useMemo(
    () => (oauth2App?.permissions ? !isAppEnabledForUser(oauth2App.permissions, userRoleIds) : false),
    [oauth2App?.permissions, userRoleIds],
  )

  const classifiedError: ClassifiedError | null = useMemo(() => {
    if (authorizeError) {
      return authorizeError
    }
    if (validationErrors.length > 0) {
      return classifyValidationErrors(validationErrors)
    }
    if (oauth2AppError && 'data' in oauth2AppError) {
      return classifyGetAppError(oauth2AppError)
    }
    if (appPermissionDenied && oauth2App) {
      return buildAppPermissionDeniedError(oauth2App.name)
    }
    if (scopesError) {
      return {
        classification: 'unknown',
        alertVariant: 'danger',
        alertTitle: 'Something went wrong',
        alertBody: 'Failed to load application permissions. Please try again later.',
        errorCode: 'scopes_fetch_failed',
        actions: 'dashboard',
      }
    }
    return null
  }, [authorizeError, validationErrors, oauth2AppError, appPermissionDenied, oauth2App, scopesError])

  useEffect(() => {
    if (classifiedError) {
      dispatch((thunkDispatch: AppDispatch, getState: () => RootState) => {
        const errorToasts = selectToasts(getState()).filter((t) => t.variant === 'error')
        errorToasts.forEach((t) => thunkDispatch(removeToast(t.id)))
      })
    }
  }, [classifiedError, dispatch])

  const screen: Screen = useMemo(() => {
    if (params && (isFetchingApp || isAppUninitialized || isFetchingMe || isFetchingPermissions || isFetchingScopes)) {
      return 'loading'
    }
    if (isAuthorizeSuccess) {
      return 'success'
    }
    if (classifiedError) {
      return 'error'
    }
    if (oauth2App && params) {
      return 'consent'
    }
    return 'error'
  }, [params, isFetchingApp, isAppUninitialized, isFetchingMe, isFetchingPermissions, isFetchingScopes, isAuthorizeSuccess, classifiedError, oauth2App])

  const buildCancelRedirectUrl = useCallback(() => {
    if (!params?.redirectUri) {
      return null
    }
    try {
      const url = new URL(params.redirectUri)
      const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:']
      if (dangerousSchemes.includes(url.protocol)) {
        return null
      }
      url.searchParams.set('error', 'access_denied')
      url.searchParams.set('error_description', 'The resource owner denied the request')
      if (params.state) {
        url.searchParams.set('state', params.state)
      }
      return url.toString()
    } catch {
      return null
    }
  }, [params])

  const handleCancel = useCallback(() => {
    const cancelUrl = buildCancelRedirectUrl()
    if (cancelUrl) {
      window.location.href = cancelUrl
    } else {
      window.location.href = '/'
    }
  }, [buildCancelRedirectUrl])

  const handleScopeToggle = useCallback(
    (scope: string) => {
      setUserToggles((prev) => {
        const next = new Map(prev)
        next.set(scope, !selectedScopes.has(scope))
        return next
      })
    },
    [selectedScopes],
  )

  const handleAccept = useCallback(async () => {
    if (!params) {
      return
    }

    const body: OAuth2AuthorizeRequestBody = {
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      response_type: params.responseType,
      scope: Array.from(selectedScopes).join(' '),
      state: params.state,
    }
    if (params.codeChallengeMethod) {
      body.code_challenge_method = params.codeChallengeMethod
    }
    if (params.codeChallenge) {
      body.code_challenge = params.codeChallenge
    }

    const response = await triggerOAuth2Authorize({ accountId, body })

    if ('error' in response && response.error) {
      if ('data' in response.error) {
        setAuthorizeError(classifyAuthorizeError(response.error as FetchBaseQueryError))
      } else {
        setAuthorizeError({
          classification: 'authorize_failed',
          alertVariant: 'danger',
          alertTitle: 'Something went wrong with the connection',
          alertBody: "This is a setup issue on the connecting app's side. Contact your account admin or the app's support team for help.",
          errorCode: 'message' in response.error ? String(response.error.message) : 'Unknown error',
          actions: 'cancel_try_again',
        })
      }
      return
    }
    if (response.data) {
      setSuccessRedirectUri(response.data.data.redirectUri)
      window.location.href = response.data.data.redirectUri
    }
  }, [params, selectedScopes, triggerOAuth2Authorize, accountId])

  const handleTryAgain = useCallback(() => {
    setAuthorizeError(null)
    handleAccept()
  }, [handleAccept])

  return (
    <div className='oauth-page d-flex justify-content-center min-vh-100 p-3 pt-5'>
      <div className='oauth-content'>
        {screen === 'loading' && (
          <div className='text-center'>
            <Spinner />
          </div>
        )}

        {screen === 'consent' && oauth2App && params && (
          <OAuthConsentView
            oauth2App={oauth2App}
            scopeGroups={buildScopeGroups(availableScopes ?? [], permissions)}
            selectedScopes={selectedScopes}
            onScopeToggle={handleScopeToggle}
            userName={userName}
            isAuthorizing={isAuthorizing}
            onAccept={handleAccept}
            onCancel={handleCancel}
          />
        )}

        {screen === 'success' && <OAuthSuccessView appName={oauth2App?.name ?? 'the app'} redirectUri={successRedirectUri ?? '/'} />}

        {screen === 'error' && classifiedError && (
          <OAuthErrorView
            classifiedError={classifiedError}
            appName={oauth2App?.name}
            appImageUrl={oauth2App?.imageUrl}
            userName={userName}
            onCancel={handleCancel}
            onTryAgain={handleTryAgain}
          />
        )}
      </div>
    </div>
  )
}
